import {Component, inject, Input} from '@angular/core';
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ILotteryLicenseReport} from "../../../interfaces/reporting/ILotteryLicenseReport";
import {ReportingService} from "../../../services/reporting.service";
import {
  ComponentLevelErrorMessageComponent
} from "../../display-errors/component-level-error-message/component-level-error-message.component";
import {DatePipe} from "@angular/common";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {PipesModule} from "../../../pipes/pipes.module";
import {MatListModule} from "@angular/material/list";
import {MatButton} from "@angular/material/button";
import {Content, TDocumentDefinitions} from "pdfmake/interfaces";
import * as pdfMake from "pdfmake/build/pdfmake";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {PdfUtilityService} from "../../../services/pdf-utility.service";
import {GameTypePipe} from "../../../pipes/game-type.pipe";
import {MatLine} from "@angular/material/core";
import {IGameLotteryLicence} from "../../../interfaces/IGameLotteryLicence";
import {ILotteryLicenseReportingData} from "../../../interfaces/reporting/ILotteryLicenseReportingData";

@Component({
  selector: 'app-lottery-license-report',
  standalone: true,
  templateUrl: './lottery-license-report.component.html',
  imports: [
    ComponentLevelErrorMessageComponent,
    DatePipe,
    LoadingCardContentComponent,
    PipesModule,
    MatListModule,
    MatButton,
    MatLine
  ],
  styleUrls: ['./lottery-license-report.component.scss', '../../../pages/reports/reports.scss',]
})
export class LotteryLicenseReportComponent {

  private reportingService: ReportingService = inject(ReportingService)
  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe)
  private gameTypePipe: GameTypePipe = inject(GameTypePipe)
  private pdfUtilityService: PdfUtilityService = inject(PdfUtilityService)
  private datePipe: DatePipe = inject(DatePipe)

  @Input() set reportingDataSetter(valueP: ILotteryLicenseReportingData) {
    this.reportingData = valueP;
    this.loadReport();
  }

  public uiState = UIStateEnum.ShowLoading;
  public uiStateForTemplate = UIStateEnum;
  public errorMessage = '';
  public reportingData!: ILotteryLicenseReportingData;
  public lotteryLicenseReport!: ILotteryLicenseReport;

  private loadReport() {
    if (this.reportingData) {
      this.reportingService.getLotteryLicenseReport(
        this.reportingData.GameId,
        this.reportingData.LotteryLicenseId,
        this.reportingData.StartDate,
        this.reportingData.EndDate
      ).subscribe({
        next: (comprehensiveReportResP) => {
          this.lotteryLicenseReport = comprehensiveReportResP;
          this.uiState = UIStateEnum.ShowData;
        },
        error: () => {
          this.errorMessage = 'There was an issue retrieving your lottery license report. ' +
            'Please try refreshing the page or reaching out to a system administrator.';
          this.uiState = UIStateEnum.ShowData;
        }
      });
    }
  }

  generatePDF() {
    const contentP: Content[] = [];
    if (this.lotteryLicenseReport) {

      contentP.push({
        text: `Lottery License #${this.lotteryLicenseReport.LicenceNumber.LicenceNumber}`,
        style: this.pdfUtilityService.PdfStyles.pageTitle,
        margin: [0, 0, 0, 8]
      });
      contentP.push({
        text: `Date Range: ${this.datePipe.transform(this.reportingData.StartDate, 'yyyy-MM-dd', 'UTC')} -
        ${this.datePipe.transform(this.reportingData.EndDate, 'yyyy-MM-dd', 'UTC')}`,
        style: this.pdfUtilityService.PdfStyles.pageTitle,
        margin: [0, 0, 0, 8]
      });

      contentP.push(this.getFinancialDetails());
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getWinners());
      contentP.push(this.pdfUtilityService.AddLineBreak());

      const docDef: TDocumentDefinitions = {
        pageMargins: [40, 40, 40, 40],
        footer: (currentPage, pageCount) => {
          return {
            text: 'Page ' + currentPage.toString() + ' of ' + pageCount,
            style: this.pdfUtilityService.PdfStyles.footer
          };
        },
        content: contentP
      };

      const pdf = pdfMake.createPdf(docDef);

      pdf.open();
    }
  }

  private getFinancialDetails(): Content[] {
    const financialDetails: Content[] = [];
    financialDetails.push(this.pdfUtilityService.generateSectionTitle('Lottery License Report'));
    const columnOne = [
      this.pdfUtilityService.generateLabelValueRow('Game Name: ', `${this.lotteryLicenseReport.GameName}`),
      this.pdfUtilityService.generateLabelValueRow('Game Type: ', `${this.gameTypePipe.transform(this.lotteryLicenseReport.GameType)}`),
      this.pdfUtilityService.generateLabelValueRow('Tickets Sold #: ', `${this.lotteryLicenseReport.TicketsSold}`),
      this.pdfUtilityService.generateLabelValueRow('Total Gross Sales: ', `${this.currencyPipe.transform(this.lotteryLicenseReport.TotalGrossSales)}`),
      this.pdfUtilityService.generateLabelValueRow('Total Expenses: ', `${this.currencyPipe.transform(this.lotteryLicenseReport.TotalExpenses)}`),
      this.pdfUtilityService.generateLabelValueRow('Net Proceeds Raised for Charity: ', `${this.currencyPipe.transform(this.lotteryLicenseReport.NetProceedsForCharity)}`),
    ];

    // The PDF is the copy that actually reaches the licensing body, so it carries the same reconciliation the screen
    // does rather than the two bare totals it used to show (#1517).
    const potReconciliation = [
      this.pdfUtilityService.generateLabelValueRow('Pot at the Start of the Period (rolled over, or the seed for a first draw): ', `${this.currencyPipe.transform(this.lotteryLicenseReport.PotCarriedIn)}`),
      this.pdfUtilityService.generateLabelValueRow('Allocated to Player Winnings This Period (approximately half of gross sales; each payment rounds its share up): ', `${this.currencyPipe.transform(this.lotteryLicenseReport.AmountAllocatedToPlayerWinnings)}`),
      ...(this.lotteryLicenseReport.PotAdjustments !== 0
        ? [this.pdfUtilityService.generateLabelValueRow('Seeds, Refunds and Other Pot Adjustments: ', `${this.currencyPipe.transform(this.lotteryLicenseReport.PotAdjustments)}`)]
        : []),
      this.pdfUtilityService.generateLabelValueRow('Total Amount Awarded To Winners: ', `${this.currencyPipe.transform(this.lotteryLicenseReport.TotalPrizesAwarded)}`),
      this.pdfUtilityService.generateLabelValueRow('Pot Carried Forward To Next Period (drawn but unclaimed): ', `${this.currencyPipe.transform(this.lotteryLicenseReport.PotCarriedForward)}`),
    ];

    const gameColumns: Content = {
      columns: [
        columnOne
      ]
    };
    financialDetails.push(gameColumns);

    financialDetails.push(this.pdfUtilityService.generateSectionTitle('Prize Pot Reconciliation'));
    financialDetails.push({ columns: [potReconciliation] });
    financialDetails.push(this.pdfUtilityService.generateLabelValueRow(
      'Note: ',
      'Start of period + allocated + adjustments = awarded + carried forward. Only draws that have been held are included, and a draw belongs to the period its scheduled end date falls in. A period is therefore not final until every draw scheduled in it has been held - re-running this report after a late draw completes will not match a copy filed before it.'));

    return financialDetails;
  }

  private getWinners(): Content[] {
    const winnerRows: Content[] = [];
    let winners = this.lotteryLicenseReport.Winners;

    winnerRows.push(this.pdfUtilityService.generateSectionTitle('Winners'));

    const winnerContent: Content = {
      layout: 'lightHorizontalLines',
      table: {
        headerRows: 1,
        body: [
          [
            {
              text: 'Name',
              fontSize: 10
            },
            {
              text: 'Phone',
              fontSize: 10
            },
            {
              text: 'Winnings',
              fontSize: 10
            },
            {
              text: 'Draw Date',
              fontSize: 10
            },
          ]
        ]
      },
      margin: [0, 16, 0, 16]
    };

    if (winners && winners?.length > 0) {
      winners.forEach((winnerP) => {
        winnerContent.table.body.push([
          winnerP.FirstName + ' ' + winnerP.LastName, winnerP.Phone, `${this.currencyPipe.transform(winnerP.PlayerWinnings)}`, `${this.datePipe.transform(winnerP.EndedOn, 'yyyy-MM-dd')}`
        ]);
      });
    }

    winnerRows.push(winnerContent);
    return winnerRows;
  }

}
